import { useRouter } from "@/app/hooks/useRouter";
import DuplicateTabDetectionModal from "@/app/routes/auth_routes/EMRCreationPage/DuplicateTabDetectionModal";
import { ChevronDown, Mic } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";
import AllPreviousEMRModal from "./AllPreviousEMRModal";

const VOICE_NUDGE_DURATION_MS = 30000;

const formatVoiceDuration = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, "0")}`;
};

const NavigationComponent = ({
  setShowMedicalHistory,
  emrDetails,
  // Optional: when omitted the "Voice to EMR" button is hidden (recording is
  // driven only by the app-root floating widget).
  setShowVoicePanel = null,
  isVoicePanelOpen = false,
  voiceDuration = 0,
}) => {
  const { getRouteKey } = useRouter();

  const [nudgeVoiceButton, setNudgeVoiceButton] = useState(false);
  const nudgeSpentRef = useRef(false);

  useEffect(() => {
    if (nudgeSpentRef.current) return;
    // Already open — auto-start probably got here first, so there is nothing to
    // remind anyone about.
    if (isVoicePanelOpen) {
      nudgeSpentRef.current = true;
      setNudgeVoiceButton(false);
      return;
    }
    setNudgeVoiceButton(true);
    const timer = setTimeout(() => {
      setNudgeVoiceButton(false);
      nudgeSpentRef.current = true;
    }, VOICE_NUDGE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isVoicePanelOpen]);

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");

  const customerDetails = emrDetails?.customer_details;
  const petDetails = emrDetails?.pet_details;
  const isHighRiskPatient =
    emrDetails?.high_risk_patient ?? petDetails?.high_risk_patient ?? customerDetails?.high_risk_patient;

  const navigate = useNavigate();
  const [isDuplicateTabDetected, setIsDuplicateTabDetected] = useState<boolean>(false);
  const [showAllPreviousEMR, setShowAllPreviousEMR] = useState<boolean>(false);

  const HandelVisitProfile = () => {
    navigate(`${getRouteKey("EMR_TAB", "url")}/${customerDetails?.customer_id}/${petDetails?.pet_id}`);
  };

  useEffect(() => {
    const key = `editing-emr-${emr_id}`;
    const tabId = Date.now().toString();
    const channel = new BroadcastChannel("emr-alert");
    const existingTabId = localStorage.getItem(key);
    localStorage.setItem(key, tabId);
    if (existingTabId && existingTabId !== tabId) {
      setIsDuplicateTabDetected(true);
      channel.postMessage({ type: "EMR_CONFLICT", emrId: emr_id });
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== tabId) {
        setIsDuplicateTabDetected(true);
      }
    };

    channel.onmessage = (event) => {
      if (event.data?.type === "EMR_CONFLICT" && event.data.emrId === emr_id) {
        setIsDuplicateTabDetected(true);
      }
    };
    window.addEventListener("storage", onStorage);
    const onUnload = () => {
      if (localStorage.getItem(key) === tabId) {
        localStorage.removeItem(key);
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("beforeunload", onUnload);
      channel.close();

      if (localStorage.getItem(key) === tabId) {
        localStorage.removeItem(key);
      }
    };
  }, [emr_id, navigate]);

  return (
    <>
      <div className="sticky top-12 z-30 w-full mx-auto">
        <div className="w-full ">
          <div className={`w-full rounded-[10px] ${isHighRiskPatient ? "bg-[#fff5f6]" : "bg-white"}  shadow-sm`}>
            <div className="flex flex-col gap-2 py-3 px-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 text-sm min-w-0">
                <span
                  className="flex items-center gap-2 text-blue-500 font-medium cursor-pointer tracking-tight42"
                  onClick={HandelVisitProfile}
                >
                  {customerDetails?.customer_name}
                  <span className="text-[#6C727F] font-semibold">/</span>
                </span>

                <span className="font-medium  text-black tracking-tight42 ">
                  <span className="capitalize">{petDetails?.pet_name}</span>'s EMR
                  <PetBirthdayIcon hasBirthday={petDetails?.has_birthday} petName={petDetails?.pet_name} />
                </span>
                {isHighRiskPatient && (
                  <div className="sm:pl-5">
                    <p className="bg-[#fff5f6] rounded-2xl text-center px-5 py-2 text-[#d92500] font-medium">
                      {isHighRiskPatient}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex w-full flex-row gap-2 sm:w-auto sm:gap-3 sm:shrink-0">
                <button
                  onClick={() => setShowMedicalHistory((prev) => !prev)}
                  className={`flex flex-1 items-center justify-center gap-1 whitespace-nowrap text-[11px] font-medium text-[#0B6EFF] sm:flex-none sm:justify-start sm:gap-[6px] sm:text-sm ${isHighRiskPatient ? "bg-white" : "bg-[#F7F7F7]"} rounded-lg py-[6px] px-2 sm:px-3 tracking-tight42`}
                >
                  View Medical History <ChevronDown className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                </button>
                
                {setShowVoicePanel && (
                  <button
                    onClick={() => setShowVoicePanel((prev) => !prev)}
                    className={`flex flex-1 items-center justify-center gap-1 whitespace-nowrap text-[11px] font-medium rounded-lg py-[6px] px-2 tracking-tight42 transition sm:flex-none sm:justify-start sm:gap-[6px] sm:px-3 sm:text-sm ${
                      isVoicePanelOpen
                        ? "bg-[#0B6EFF] text-white hover:bg-[#0a5ed8]"
                        : `text-[#0B6EFF] ${isHighRiskPatient ? "bg-white" : "bg-[#F7F7F7]"} hover:bg-[#eef4ff]`
                      } ${nudgeVoiceButton
                        ? "!bg-[#0B6EFF] !text-white animate-voice-nudge motion-reduce:animate-none"
                        : ""
                      }`}
                  >
                    <Mic className="h-4 w-4 shrink-0" />
                    Voice to EMR
                    {voiceDuration > 0 && (
                      <span
                        className={`ml-1 rounded px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums ${isVoicePanelOpen || nudgeVoiceButton
                            ? "bg-white/20 text-white"
                            : "bg-[#0B6EFF]/10 text-[#0B6EFF]"
                          }`}
                      >
                        {formatVoiceDuration(voiceDuration)}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DuplicateTabDetectionModal setOpen={setIsDuplicateTabDetected} open={isDuplicateTabDetected} />
      <AllPreviousEMRModal
        open={showAllPreviousEMR}
        onClose={() => setShowAllPreviousEMR(false)}
        customerId={customerDetails?.customer_id}
        petId={petDetails?.pet_id}
        currentEmrId={emr_id}
      />
    </>
  );
};
export default NavigationComponent;
